<template>
  <div class="home-page-value">
    <div class="value-row fun-value">
      <div class="container-lg">
        <h1>Easy and fun to explore</h1>
        <h4>Make your tree into a personalized learning experience for each family member.</h4>
        <div class="details">
          <div class="visual">
            <img src="@/assets/icons/sparkle.svg"/>
            <img src="@/assets/icons/sparkle.svg"/>
            <img src="@/assets/icons/star.svg"/>
            <img src="@/assets/icons/star.svg"/>
            <img src="@/assets/icons/star.svg"/>
          </div>
          <ul class="list-unstyled value-list">
            <li>A relationship map helps them see how each person is related to them.</li>
            <li>They can explore names and places important to their family.</li>
            <li>We automatically highlight interesting facts we discovered in their tree.</li>
            <li>Which ancestors have a twin? What was the population of great-grandma's hometown when she was born?</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="value-row content-value">
      <div class="container-lg">
        <h1>Photos and documents</h1>
        <h4>It's easy to let your family explore your whole collection.</h4>
        <div class="details">
          <div class="visual">
            <img src="@/assets/icons/document.svg" class="doc"/>
            <img src="@/assets/icons/photo_sepia.svg" class="photo"/>
            <img src="@/assets/icons/document.svg" class="doc"/>
            <img src="@/assets/icons/photo_sepia.svg" class="photo"/>
            <img src="@/assets/icons/photo_sepia.svg" class="photo"/>
            <img src="@/assets/icons/photo_sepia.svg" class="photo"/>
          </div>
          <ul class="list-unstyled value-list">
            <li>View original documents you've collected about your ancestors.</li>
            <li>Your linked photos and documents are all uploaded automatically.</li>
            <li>Zoom in to see photos at their original quality.</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="value-row connected-value">
      <div class="container-lg">
        <h1>Related trees are connected</h1>
        <h4>We automatically connect your tree to the trees for the rest of their family.</h4>
        <div class="details">
          <div class="visual">
            <img class="tree-arm" src="@/assets/icons/connected_tree_arm.svg"/>
            <img class="tree-arm" src="@/assets/icons/connected_tree_arm.svg"/>
            <img class="tree-base" src="@/assets/icons/connected_tree_base.svg"/>
          </div>
          <ul class="list-unstyled value-list">
            <li>Each person sees a single, connected view that includes all the trees shared with them.</li>
            <li>Works seamlessly even if the trees were created by different users using different programs.</li>
            <li>Overlapping trees are consolidated for viewing.</li>
            <li>The trees themselves are never "merged".</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="value-row privacy-value">
      <div class="container-lg">
        <h1>Personal privacy controls</h1>
        <h4>Each person can decide what to share about themselves, and who to share it with.</h4>
        <div class="details">
          <div ref="privacyVisualRef" class="visual">
            <!-- copied this asset inline so we can animate the path -->
            <svg xmlns="http://www.w3.org/2000/svg" class="shield"
                viewBox="1944.17 850.17 512.67 514.67"
                preserveAspectRatio="xMidYMid meet"
                width="100%" height="100%">
                <path ref="shieldPathRef" d="M2200.5 868.5 2246.38 890.534C2292.58 910.017 2341.08 925.145 2391.36 935.403L2438.5 942.575 2438.5 1020.02C2438.5 1163.12 2351.23 1285.9 2226.86 1338.34L2200.5 1346.5 2174.14 1338.34C2049.77 1285.9 1962.5 1163.12 1962.5 1020.02L1962.5 942.575 2009.64 935.403C2059.92 925.145 2108.42 910.017 2154.62 890.534L2200.5 868.5Z"
                    stroke="#0070C0" stroke-width="36.6667" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" fill="none" fill-rule="evenodd"
                    stroke-dasharray="10000" stroke-dashoffset="10000"/> <!-- dash used for JS animation, avoid flash by forcing a desired initial state -->
            </svg>
            <img src="@/assets/icons/person_gray_150.png" class="person"/>
          </div>
          <ul class="list-unstyled value-list">
            <li>When you share your tree with someone, they also get full control of their own profile.</li>
            <li>Changes they make to their profiles don't affect the file on your computer.</li>
            <li>Profiles are only visible to people in the same Group.</li>
            <li>Parents can control what information is shared in their children's profiles.</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="value-row easy-value">
      <div class="container-lg">
        <h1>Direct upload</h1>
        <h4>Use our <router-link to="/learn/sync">Sync app</router-link> to upload directly from your genealogy database.</h4>
        <AppSupportGrid icon-only/>
        <div class="details">
          <SyncDiagram small motion class="visual"/>
          <ul class="list-unstyled value-list">
            <li>No export to GEDCOM or merging into a global tree.</li>
            <li>Photos and documents are uploaded automatically.</li>
            <li>You control which living people are included.</li>
            <li>Only one click to update your tree later, too.</li>
          </ul>
        </div>
      </div>
    </div>
    <!-- <div class="value-row gift-value">
      <div class="container-lg">
        <h1>It's just for them</h1>
        <h4>Invite your whole family. It's always free for them.</h4>
        <div class="details">
          <GiftVisual class="visual"/>
          <ul class="list-unstyled">
            <li>Just one click to accept your invitation, and they'll instantly be exploring their family tree online!</li>
            <li>They'll find everything is designed to make it easy and fun to learn about their ancestors.</li>
          </ul>
        </div>
      </div>
    </div> -->
  </div>
</template>

<style lang="scss" scoped>
@use "@/mixins" as mix;

.home-page-value {
  display: flex;
  flex-direction: column;
  padding: 0 0.5rem;

  --page-bg-alt2: #f8f8f8;
}

.value-row {
  padding: 4rem 0;

  @media (min-width: 768px) {
    padding: 8rem 0;
  }

  .container-lg {
    margin: 0 auto;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }

  h1, h4 {
    margin: 0;
    text-align: center;
  }

  h4 {
    font-size: 1.2rem;
    font-weight: normal;
  }

  .details {
    margin-top: 3rem;
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 2rem;
    justify-items: center;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 2fr;      
      column-gap: 1rem;
      align-items: center;
    }

    & > visual {
      justify-self: center;
    }
  }

  .visual {
    --reveal-p: 0; // start in initial state
  }

  ul.value-list {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    line-height: 1.4;
    color: #333;

    li {
      position: relative;
      padding-left: 30px;

      &::before {
        content: "";
        position: absolute;
        top: 5px;
        left: 0;
        width: 18px;
        height: 18px;
        background-image: url("@/assets/icons/arrow_bullet.svg");
        background-repeat: no-repeat;
        background-size: contain;
        filter: brightness(0.9);
      }
    }
  }
}

.easy-value {
  background-color: var(--page-bg-alt2);

  .app-support-grid {
    margin-top: 1rem;
  }

  .visual {
    margin-bottom: 1rem;
    height: 70px;

    @media (min-width: 768px) {    
      margin: 0;
    }
  }

  .details {
    margin-top: 1rem;
  }

}

.content-value {
  .details {
      column-gap: 3rem;
  }

  .visual {
    @include mix.reveal-range(--reveal-p, --t, 0.5, 1);

    position: relative;
    height: 180px;
    width: 220px;

    img {
      position: absolute;
      width: 100px;
      will-change: transform;

      &:nth-child(1) { // doc
        top: 50px;
        left: calc(var(--t) * -40% + 10%);
        transform: rotate(calc(var(--t) * -20deg));
      }

      &:nth-child(2) { // photo
        top: 60px;
        left: calc(var(--t) * -10% + 20%);
        transform: rotate(calc(var(--t) * 15deg));
      }
    
      &:nth-child(3) { // doc
        top: 40px;
        left: calc(var(--t) * 10% + 35%);
        transform: rotate(calc(var(--t) * 10deg));
      }
    
      &:nth-child(4) { // photo
        top: 90px;
        left: calc(var(--t) * 20% + 40%);
        transform: rotate(calc(var(--t) * 20deg));
      }
    
      &:nth-child(5) { // photo
        top: 80px;
        left: calc(var(--t) * -30% + 15%);
        transform: rotate(calc(var(--t) * -10deg));
      }

      &:nth-child(6) { // photo
        top: 100px;
        left: calc(var(--t) * -10% + 25%);
      }
    }
  }
}

.privacy-value {
  .visual {
    position: relative;
    height: 170px;
    width: 100%;
  }

  .shield {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .person {
    position: absolute;
    top: 47%;
    left: 50%;
    width: 30%;
    transform: translate(-50%, -50%);

    @media (min-width: 768px) {
      width: 35%;
    }
  }
}

.connected-value {
  background-color: var(--page-bg-alt);

  .visual {
    position: relative;
    height: 180px;
    width: 200px;
  }

  .tree-arm {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 75px;
    width: 75px;
    filter: invert(1) brightness(0.7);
  }

  .visual :nth-child(2) {
    top: 95px;
  }

  .tree-base {
    @include mix.reveal-range(--reveal-p, --t, 0.6, 1);
    position: absolute;
    top: 36px;
    width: 79px;
    opacity: var(--t);
    filter: invert(1) brightness(0.5) sepia(40%) saturate(10) hue-rotate(-8deg);
    transform: translateX(calc((1 - var(--t)) * -80px));
    will-change: opacity, transform;
  }
}

.fun-value {
  background-color: var(--page-bg-alt2);

  .visual {
    display: inline-block;
    position: relative;
    width: 180px;
    height: 180px;
    transform: translateY(16px);

    img {
      position: absolute;
    }

    :nth-child(1) {
      @include mix.reveal-range(--reveal-p, --t, 0.3, 0.4);
      top: 0;
      left: 14%;
      width: 21%;
      transform: scale(calc(0.6 + var(--t) * 0.4));
      opacity: var(--t);
      filter: brightness(0) saturate(100%) invert(61%) sepia(97%) saturate(453%) hue-rotate(356deg) brightness(106%) contrast(110%);
    }
    :nth-child(2) {
      @include mix.reveal-range(--reveal-p, --t, 0.5, 0.6);
      top: 65%;
      left: 28%;
      width: 25%;
      transform: scale(calc(0.6 + var(--t) * 0.4));
      opacity: var(--t);
      filter: brightness(0) saturate(100%) invert(61%) sepia(97%) saturate(453%) hue-rotate(356deg) brightness(106%) contrast(110%);
      //filter: brightness(0) saturate(100%) invert(54%) sepia(28%) saturate(6035%) hue-rotate(241deg) brightness(100%) contrast(102%);      
    }
    :nth-child(3) {
      @include mix.reveal-range(--reveal-p, --t, 0.6, 0.7);
      top: 65%;
      left: 67%;
      width: 17%;
      transform: scale(calc(0.6 + var(--t) * 0.4));
      opacity: var(--t);
      filter: brightness(0) saturate(100%) invert(47%) sepia(89%) saturate(2201%) hue-rotate(80deg) brightness(104%) contrast(108%);
    }
    :nth-child(4) {
      @include mix.reveal-range(--reveal-p, --t, 0.8, 0.9);
      top: 34%;
      left: 0;
      width: 35%;
      transform: scale(calc(0.6 + var(--t) * 0.4));
      opacity: var(--t);
      //filter: brightness(0) saturate(100%) invert(20%) sepia(65%) saturate(7497%) hue-rotate(211deg) brightness(97%) contrast(91%);
      filter: brightness(0) saturate(100%) invert(50%) sepia(69%) saturate(2347%) hue-rotate(162deg) brightness(100%) contrast(101%);
    }
    :nth-child(5) {
      @include mix.reveal-range(--reveal-p, --t, 0.9, 1);
      left: 42%;
      width: 53%;
      transform: scale(calc(0.6 + var(--t) * 0.4));
      opacity: var(--t);
      filter: brightness(0) saturate(100%) invert(80%) sepia(40%) saturate(6937%) hue-rotate(341deg) brightness(96%) contrast(88%);
      //filter: brightness(0) saturate(100%) invert(57%) sepia(16%) saturate(6192%) hue-rotate(344deg) brightness(99%) contrast(105%);
    }
  }
}

.gift-value {
  .visual {
    height: 180px;
  }
}

.accent-text {
  color: var(--brand-primary);
}
</style>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { workspaceTypeInfos } from '@/rd/ResearchDataModel'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import SyncDiagram from '@/sync/SyncDiagram.vue'
import AppSupportGrid from './AppSupportGrid.vue'

const privacyVisualRef = ref<HTMLElement>()
const shieldPathRef = ref<SVGPathElement>()

const supportedAppsRef = computed(() => workspaceTypeInfos.filter(t => (t.supportedOn.length || t.plannedOn.length) && !t.hidden))

const triggers: ScrollTrigger[] = []
let shieldPathLength = 0

onMounted(() => {
  shieldPathLength = shieldPathRef.value?.getTotalLength() ?? 0
  shieldPathRef.value!.style.strokeDasharray = String(shieldPathLength)
  shieldPathRef.value!.style.strokeDashoffset = String(shieldPathLength)

  const rows = gsap.utils.toArray<HTMLElement>('.value-row')
  rows.forEach(elem => addScrollTrigger(elem, (self) => {
      elem.style.setProperty('--reveal-p', String(self.progress))
  }))

  const visuals = gsap.utils.toArray<HTMLElement>('.value-row .details .visual')
  visuals.forEach(elem => addScrollTrigger(elem, (self) => {
      elem == privacyVisualRef.value
        ? updateShieldReveal(self.progress)
        : elem.style.setProperty('--reveal-p', String(self.progress))
  }))
})

function addScrollTrigger(elem: HTMLElement, onUpdate: (self: ScrollTrigger) => void) {
  const t = ScrollTrigger.create({
    trigger: elem,
    start: 'top 95%',
    end: 'top 65%',
    scrub: true,
    onUpdate
  })
  triggers.push(t)
}

function updateShieldReveal(p: number) {
  const start = 0.2
  const end = 1
  const t = Math.min(1, Math.max(0, (p - start) / (end - start)))
  shieldPathRef.value!.style.strokeDashoffset = String(shieldPathLength * (1 - t))
}

onBeforeUnmount(() => {
  triggers.forEach(t => t.kill())
  triggers.length = 0
})

</script>
